import { DetailCard, type DetailCardRow, Icon, SKELETON_ROW_HEIGHT, SkeletonLoader } from '@civilgrid/ui/components';
import { Link } from '@tanstack/react-router';
import { memo, type ReactNode } from 'react';
import type { IntelligenceSummary } from '~/common/types';
import HoverCard from '~/components2/HoverCard';
import { hasPriorComputeData, isMissingDates } from '../conflictsSummary/common';

/** Placeholder for cells whose value is being saved or recomputed, matching the grid's fetch-state skeletons. */
export const CellSkeleton = ({ 'data-testid': dataTestId }: { 'data-testid'?: string }) => {
  return (
    <div className="flex w-full items-center" data-testid={dataTestId}>
      <SkeletonLoader height={SKELETON_ROW_HEIGHT} className="flex-1" />
    </div>
  );
};

/** Hover card rendered as a DetailCard, matching the intelligence table's FeatureDetailCard. */
export const DetailHoverCard = ({
  trigger,
  rows,
  children,
}: {
  trigger: ReactNode;
  rows?: DetailCardRow[];
  children?: ReactNode;
}) => {
  return (
    <HoverCard unstyled trigger={trigger}>
      <DetailCard rows={rows} className="w-72">
        {children}
      </DetailCard>
    </HoverCard>
  );
};

/** Hover cell whose card shows a bold warning title above body text. */
const WarningHoverCell = ({ trigger, title, body }: { trigger: ReactNode; title: ReactNode; body: ReactNode }) => {
  return (
    <DetailHoverCard trigger={trigger}>
      <div className="flex flex-col">
        <span className="flex items-center gap-1 font-semibold">{title}</span>
        <span>{body}</span>
      </div>
    </DetailHoverCard>
  );
};

/**
 * V2 versions of the classic page's cell states (conflictsSummary/common):
 * same triggers and data-testids, but the hover content renders through
 * DetailHoverCard.
 */
export const MissingInfo = ({
  projectId,
  'data-testid': dataTestId,
}: {
  projectId: string;
  'data-testid'?: string;
}) => {
  return (
    <WarningHoverCell
      trigger={
        <span className="inline-flex items-center gap-1 rounded-sm px-1 text-red-500" data-testid={dataTestId}>
          <Icon icon="luCloudAlert" size={14} className="shrink-0" />
          Missing Dates
        </span>
      }
      title={
        <>
          <Icon icon="luCloudAlert" size={14} className="shrink-0" />
          Missing Construction Dates
        </>
      }
      body={
        <>
          Add a construction start and end date to compute this project's intelligence.{' '}
          <Link
            to="/projects/$projectId/$tabId"
            params={{ projectId, tabId: 'details' }}
            className="text-blue-50 underline hover:text-blue-60"
          >
            Update Project Info
          </Link>
          .
        </>
      }
    />
  );
};

export const StaleCell = ({ value, 'data-testid': dataTestId }: { value: ReactNode; 'data-testid'?: string }) => {
  return (
    <WarningHoverCell
      trigger={
        <span className="inline-flex items-center gap-1 rounded-sm px-1" data-testid={dataTestId}>
          <Icon icon="luCloudAlert" size={14} className="shrink-0" ariaLabel="Stale data" />
          {value}
        </span>
      }
      title={
        <>
          <Icon icon="luCloudAlert" size={14} className="shrink-0" />
          Stale data
        </>
      }
      body="This summary failed to refresh and may not reflect recent changes. We're looking into it."
    />
  );
};

export const EmptyCell = () => {
  return (
    <WarningHoverCell
      trigger={<span className="inline px-1 italic text-grey-50">—</span>}
      title={
        <>
          <Icon icon="luCloudAlert" size={14} className="shrink-0" />
          No data available
        </>
      }
      body="The compute intelligence job failed and no prior data is available."
    />
  );
};

/**
 * Shared compute-status cascade for summary cells: missing dates, processing,
 * and error states render their placeholder cells; `children` renders the ok
 * state (deferred so it only evaluates when reachable).
 */
export const ComputeStateCell = ({
  summary,
  staleValue,
  'data-testid': dataTestId,
  children,
}: {
  summary: IntelligenceSummary;
  /** Value the stale-data state shows next to the warning icon; '—' when empty. */
  staleValue?: ReactNode;
  'data-testid'?: string;
  children: () => ReactNode;
}) => {
  if (isMissingDates(summary)) {
    return <MissingInfo projectId={summary.project.id} data-testid={dataTestId} />;
  }

  if (summary.computeStatus === 'processing') {
    return <CellSkeleton data-testid={dataTestId} />;
  }

  if (summary.computeStatus === 'error') {
    if (!hasPriorComputeData(summary)) {
      return <EmptyCell />;
    }
    return <StaleCell value={staleValue == null || staleValue === '' ? '—' : staleValue} data-testid={dataTestId} />;
  }

  return children();
};

export const Cell = memo(
  ({
    value,
    warning,
    summary,
    tooltipRows,
    'data-testid': dataTestId,
  }: {
    value: number | string;
    warning?: boolean;
    summary: IntelligenceSummary;
    /** Label/value rows for the hover card (FeatureDetailCard style). */
    tooltipRows?: DetailCardRow[];
    'data-testid'?: string;
  }) => {
    return (
      <ComputeStateCell summary={summary} staleValue={value} data-testid={dataTestId}>
        {() => {
          if (warning) {
            return (
              <WarningHoverCell
                trigger={
                  <span className="inline rounded-sm bg-yellow-100 px-1 text-yellow-900" data-testid={dataTestId}>
                    {value}
                  </span>
                }
                title={
                  <>
                    <Icon icon="luCloudAlert" size={14} className="shrink-0" />
                    NOTE:
                  </>
                }
                body="This project contains non-digitized data for some agencies."
              />
            );
          }

          const content = (
            <div className="px-1" data-testid={dataTestId}>
              {value}
            </div>
          );

          if (tooltipRows) {
            return <DetailHoverCard trigger={content} rows={tooltipRows} />;
          }

          return content;
        }}
      </ComputeStateCell>
    );
  },
);
